import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Loader2, LifeBuoy, ExternalLink, Settings2, Info } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * SupportAdmin — thin host for a third-party support widget.
 * The HTML/JS pasted in Admin → Settings → "Widget de support" is injected into a
 * sandboxed iframe (srcdoc) so arbitrary scripts cannot access the parent DOM,
 * localStorage, cookies or the API of this app.
 */
export default function SupportAdmin() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({})).finally(() => setLoading(false));
  }, []);

  const widget = (settings?.support_widget_html || "").trim();

  // Build a self-contained HTML document for the iframe. The widget provider's
  // script runs in a proper full-size body, exactly as on a real website.
  const srcDoc = useMemo(() => {
    if (!widget) return "";
    return `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Support</title>
  <style>
    html,body { margin:0; padding:0; height:100%; width:100%; background:#f8fafc; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; color:#1e293b; }
    body { display:flex; align-items:flex-end; justify-content:flex-end; }
    .empty-msg { margin:auto; padding:2rem; text-align:center; color:#64748b; }
  </style>
</head>
<body>
  <div class="empty-msg">Chargement de votre outil de support…</div>
  ${widget}
</body>
</html>`;
  }, [widget]);

  if (loading) {
    return <div className="p-12 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  }

  return (
    <div className="p-6 md:p-10 flex flex-col h-full" data-testid="admin-support-page">
      <header className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-1">Support</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)] flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-[color:var(--brand-sky)]" />
            Centre de support
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Votre outil de support externe s'affiche ci-dessous (Crisp, Tawk.to, Intercom, Freshdesk…). Le widget est isolé dans un iframe sécurisé.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="h-9 text-xs">
            <Link to="/admin/settings" data-testid="go-support-settings">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" />Configurer le widget
            </Link>
          </Button>
        </div>
      </header>

      {!widget ? (
        <Card className="flex-1 flex items-center justify-center p-10 border-2 border-dashed" data-testid="support-empty-state">
          <div className="max-w-lg text-center">
            <div className="w-16 h-16 rounded-2xl bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] flex items-center justify-center mx-auto mb-4">
              <LifeBuoy className="w-8 h-8" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">Aucun outil de support configuré</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">
              Rendez-vous dans <strong>Paramètres → Widget de support</strong> et collez le code HTML/JavaScript fourni par l'outil de votre choix.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6 text-left">
              {[
                { name: "Tawk.to", url: "https://tawk.to", note: "Gratuit · illimité" },
                { name: "Crisp", url: "https://crisp.chat", note: "Freemium · FR" },
                { name: "Intercom", url: "https://intercom.com", note: "Premium" },
                { name: "Brevo Conv.", url: "https://brevo.com", note: "FR · CRM" },
                { name: "Freshdesk", url: "https://freshdesk.com", note: "Helpdesk" },
                { name: "LiveChat", url: "https://livechat.com", note: "Pro" },
              ].map((t) => (
                <a key={t.name} href={t.url} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)] hover:shadow-sm transition text-xs">
                  <div className="font-bold text-[color:var(--brand-navy)] inline-flex items-center gap-1">{t.name} <ExternalLink className="w-2.5 h-2.5" /></div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{t.note}</div>
                </a>
              ))}
            </div>
            <Button asChild className="bg-[color:var(--brand-navy)]">
              <Link to="/admin/settings#support" data-testid="configure-widget-cta">
                <Settings2 className="w-4 h-4 mr-1.5" />Configurer maintenant
              </Link>
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
            <Info className="w-3 h-3" />
            Widget tiers chargé dans un iframe sandbox — sécurité renforcée.
          </div>
          <Card className="flex-1 overflow-hidden border" style={{ minHeight: 600 }} data-testid="support-widget-iframe-wrap">
            <iframe
              title="Outil de support"
              srcDoc={srcDoc}
              className="w-full h-full min-h-[600px]"
              sandbox="allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-same-origin"
              style={{ border: 0, height: "calc(100vh - 200px)", minHeight: 600 }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
